import { useCallback, useEffect, useState } from 'react';
import { appeler, ErreurApi } from './api';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DILIGENCES — ce qui manque, ce qui bloque, ce qui reste à faire
 *                                   §23 · §24 · §26 · §36 · §37 · §38 · §47
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ CE BLOC S'AJOUTE À LA PAGE DOSSIER, IL NE LA REMPLACE PAS. Le §47 veut UNE
 * page qui répond à « où suis-je, qu'est-ce qui bloque, quelle est la prochaine
 * action ». Une seconde page « risques » et une troisième « tâches » obligeraient
 * l'agent à recoller lui-même ce que le registre sait déjà relier — et c'est
 * précisément ce qu'un logiciel de gestion classique fait mal.
 *
 * ⚠️ AUCUNE RÈGLE N'EST RECOPIÉE ICI. Le niveau d'un risque, la possibilité de
 * le lever, l'état d'une signature, le solde : tout vient du service, qui les
 * calcule au même endroit que le refus. Un écran qui trancherait lui-même
 * afficherait tôt ou tard un bouton ouvert qui rend 409.
 *
 * ⚠️ CHAQUE COMPTE PORTE SA POPULATION — « 3 sur 34 », jamais « 3 ». C'est la
 * règle du plan de travail, et elle vaut ici : « 0 risque » ne veut rien dire
 * sans savoir si l'on a regardé quelque chose. Un acte vide ne produit aucun
 * risque, ce qui n'est pas la même chose qu'un acte sûr.
 */

interface Risque {
  code: string; categorie: string; niveau: string;
  constat: string; action: string;
  levee_le: string | null; levee_motif: string | null;
}
interface Tache {
  id: string; libelle: string; detail: string | null; categorie: string;
  origine: string; risque_code: string | null; statut: string;
  echeance: string | null; assignee: string | null; en_retard: boolean;
}
interface Version {
  rang: number; document: string; edite_le: string; signatures: number;
  scelle: boolean; authentique: boolean; courante: boolean;
  statut: string; etat: string; alerte: string | null;
}

/** Les quatre niveaux, et ce qu'ils IMPLIQUENT — pas seulement leur couleur. */
const NIVEAU: Record<string, { classe: string; libelle: string; suite: string }> = {
  bloquant:  { classe: 'rouge', libelle: 'Bloquant',
               suite: "l'acte ne peut pas être reçu — un bloquant ne se lève pas, il se corrige" },
  eleve:     { classe: 'ambre', libelle: 'Élevé',
               suite: 'une levée motivée est requise pour poursuivre' },
  attention: { classe: 'bleu',  libelle: 'Attention', suite: 'à vérifier avant de signer' },
  info:      { classe: 'gris',  libelle: 'Information', suite: "à savoir ; n'empêche rien" },
};

const STATUT_TACHE: Record<string, { classe: string; libelle: string }> = {
  a_faire:    { classe: 'ambre', libelle: 'À faire' },
  en_cours:   { classe: 'bleu',  libelle: 'En cours' },
  faite:      { classe: 'vert',  libelle: 'Faite' },
  sans_objet: { classe: 'gris',  libelle: 'Sans objet' },
};

const CATEGORIE: Record<string, string> = {
  piece: 'Pièce', controle: 'Contrôle', signature: 'Signature',
  publication: 'Publication', paiement: 'Paiement', relance: 'Relance',
  cadastre: 'Cadastre', rendez_vous: 'Rendez-vous', autre: 'Autre',
};

const jour = (v: string | null) => (v ? new Date(v).toLocaleDateString('fr-FR') : '—');

export function Diligences({ reference }: { reference: string }) {
  const [risques, setRisques] = useState<any>(null);
  const [taches, setTaches] = useState<any>(null);
  const [versions, setVersions] = useState<any>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      /**
       * ⚠️ TROIS APPELS EN PARALLÈLE, ET UN ÉCHEC N'EN CACHE PAS DEUX.
       * `allSettled` et non `all` : si les versions échouent (habilitation
       * fermée sur `doc.document`), les risques et les tâches doivent
       * s'afficher quand même. Un `all` rendrait l'écran entièrement vide pour
       * une seule permission manquante — et l'agent conclurait que le dossier
       * est vide.
       */
      const [r, t, v] = await Promise.allSettled([
        appeler<any>(`/mutations/${reference}/risques`),
        appeler<any>(`/taches?mutation=${encodeURIComponent(reference)}`),
        appeler<any>(`/mutations/${reference}/versions`),
      ]);
      setRisques(r.status === 'fulfilled' ? r.value : { indisponible: motifDe(r.reason) });
      setTaches(t.status === 'fulfilled' ? t.value : { indisponible: motifDe(t.reason) });
      setVersions(v.status === 'fulfilled' ? v.value : { indisponible: motifDe(v.reason) });
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    }
  }, [reference]);

  useEffect(() => { void charger(); }, [charger]);

  async function deduire() {
    setOccupe(true); setMessage(null); setErreur(null);
    try {
      const r = await appeler<any>(`/taches/depuis-risques/${reference}`,
        { method: 'POST', body: JSON.stringify({}) });
      setMessage(r.lecture);
      await charger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    } finally { setOccupe(false); }
  }

  async function changerStatut(t: Tache, statut: string) {
    const motif = statut === 'sans_objet'
      ? window.prompt("Pourquoi cette tâche n'a-t-elle plus d'objet ? (dix caractères au moins — "
                      + "c'est ce qui sépare une tâche devenue inutile d'une liste qu'on vide.)")
      : null;
    if (statut === 'sans_objet' && (motif === null || motif.trim().length < 10)) return;
    setOccupe(true); setMessage(null); setErreur(null);
    try {
      const r = await appeler<any>(`/taches/${t.id}/statut`,
        { method: 'POST', body: JSON.stringify({ statut, motif: motif?.trim() }) });
      if (r.mention) setMessage(r.mention);
      await charger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    } finally { setOccupe(false); }
  }

  async function lever(r: Risque) {
    const motif = window.prompt(
      `Lever la réserve « ${r.code} » — pourquoi l'assumez-vous ?\n\n`
      + `Lever n'est PAS corriger : le risque reste, il est assumé, par vous, et le journal le retient.`);
    if (motif === null || motif.trim().length < 10) return;
    setOccupe(true); setMessage(null); setErreur(null);
    try {
      const x = await appeler<any>(`/mutations/${reference}/risques/${r.code}/lever`,
        { method: 'POST', body: JSON.stringify({ motif: motif.trim() }) });
      setMessage(x.mention);
      await charger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    } finally { setOccupe(false); }
  }

  return (
    <section className="dossier-diligences">
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {message && <p className="mention">{message}</p>}

      {/* ── §36 — CE QUI BLOQUE ─────────────────────────────────────────── */}
      <h4>Contrôles</h4>
      {risques?.indisponible
        ? <p className="mention">Contrôles non consultables : {risques.indisponible}</p>
        : !risques ? <p className="attente">Lecture des contrôles…</p>
        : <>
            {/**
              * ⚠️ L'INDICATEUR DE SUIVI EN TÊTE, comme sur une fiche d'étude :
              * un agent doit savoir en UN COUP D'ŒIL si l'acte peut être reçu.
              * Le détail vient après, pas avant.
              */}
            <p className={risques.compte.bloquant > 0 ? 'erreur' : 'mention'}>
              <strong>{risques.lecture}</strong>
            </p>
            {risques.risques.length > 0 && (
              <table className="tableau">
                <thead><tr>
                  <th>Niveau</th><th>Constat</th><th>Ce qu’il faut faire</th><th /> 
                </tr></thead>
                <tbody>
                  {risques.risques.map((r: Risque) => {
                    const n = NIVEAU[r.niveau] ?? NIVEAU.info!;
                    return (
                      <tr key={r.code}>
                        <td>
                          <span className={`etat ${n.classe}`}>{n.libelle}</span>
                          <br /><span className="mention">{r.categorie.toLowerCase()}</span>
                        </td>
                        <td>{r.constat}
                          {r.levee_le && (
                            /* ⚠️ UNE RÉSERVE LEVÉE RESTE AFFICHÉE. Lever n'est pas
                               corriger : la faire disparaître donnerait l'illusion
                               d'un dossier sain. */
                            <><br /><span className="mention">
                              Réserve levée le {jour(r.levee_le)} — {r.levee_motif}
                            </span></>
                          )}
                        </td>
                        <td>{r.action}<br /><span className="mention">{n.suite}</span></td>
                        <td>
                          {r.niveau === 'bloquant'
                            /* Un bouton fermé est AFFICHÉ et motivé, jamais absent. */
                            ? <button className="bouton discret" disabled
                                      title="Un bloquant ne se lève pas : il se corrige, ou l’acte n’est pas reçu.">
                                Non levable
                              </button>
                            : r.levee_le
                              ? <span className="mention">levée</span>
                              : <button className="bouton discret" disabled={occupe}
                                        onClick={() => void lever(r)}>Lever la réserve</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>}

      {/* ── §37 · §38 — CE QU’IL RESTE À FAIRE ──────────────────────────── */}
      <h4>Diligences</h4>
      {taches?.indisponible
        ? <p className="mention">Diligences non consultables : {taches.indisponible}</p>
        : !taches ? <p className="attente">Lecture des diligences…</p>
        : <>
            <div className="actions-ligne">
              <button className="bouton discret" disabled={occupe} onClick={() => void deduire()}>
                Déduire les diligences des contrôles
              </button>
              <span className="mention">
                {taches.total === 0
                  ? "Aucune diligence. Le bouton ci-dessus les déduit des contrôles ouverts."
                  : `${taches.ouvertes} ouverte(s) sur ${taches.total}`}
                {taches.en_retard > 0 && <> · <strong className="erreur-inline">{taches.en_retard} en retard</strong></>}
              </span>
            </div>
            {taches.taches.length > 0 && (
              <table className="tableau">
                <thead><tr>
                  <th>Statut</th><th>Nature</th><th>Diligence</th>
                  <th>Échéance</th><th>Chargé</th><th />
                </tr></thead>
                <tbody>
                  {taches.taches.map((t: Tache) => {
                    const s = STATUT_TACHE[t.statut] ?? STATUT_TACHE.a_faire!;
                    const ouverte = ['a_faire', 'en_cours'].includes(t.statut);
                    return (
                      <tr key={t.id}>
                        <td><span className={`etat ${s.classe}`}>{s.libelle}</span></td>
                        <td>{CATEGORIE[t.categorie] ?? t.categorie}
                          {/* ⚠️ L'ORIGINE SE VOIT : une diligence déduite se referme
                              d'elle-même quand son contrôle disparaît ; une diligence
                              décidée par quelqu'un, non. */}
                          {t.origine === 'regle' && <><br /><span className="mention">déduite</span></>}
                        </td>
                        <td>{t.libelle}
                          {t.detail && <><br /><span className="mention">{t.detail}</span></>}
                        </td>
                        <td>{t.echeance
                          ? <span className={t.en_retard ? 'erreur-inline' : ''}>{jour(t.echeance)}</span>
                          /* ⚠️ « sans terme » N'EST PAS « oubliée » : beaucoup de
                             diligences n'ont pas d'échéance, et en imposer une ferait
                             inventer des dates. */
                          : <span className="mention">sans terme</span>}</td>
                        <td>{t.assignee ?? <span className="mention">non assignée</span>}</td>
                        <td>
                          {ouverte && (
                            <>
                              <button className="bouton discret" disabled={occupe}
                                      onClick={() => void changerStatut(t, 'faite')}>Faite</button>{' '}
                              <button className="bouton discret" disabled={occupe}
                                      onClick={() => void changerStatut(t, 'sans_objet')}>Sans objet</button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>}

      {/* ── §23 · §24 — L’ACTE, SES VERSIONS, SES SIGNATURES ────────────── */}
      <h4>Versions de l’acte</h4>
      {versions?.indisponible
        ? <p className="mention">Versions non consultables : {versions.indisponible}</p>
        : !versions ? <p className="attente">Lecture des versions…</p>
        : versions.total === 0
          ? <p className="mention">{versions.lecture}</p>
          : <>
              <p className={versions.alertes > 0 ? 'erreur' : 'mention'}>{versions.lecture}</p>
              <table className="tableau">
                <thead><tr>
                  <th>Version</th><th>Document</th><th>Éditée le</th>
                  <th>Signatures</th><th>État</th>
                </tr></thead>
                <tbody>
                  {versions.versions.map((v: Version) => (
                    <tr key={v.document}>
                      <td>v{v.rang}{v.courante && <><br /><span className="mention">fait foi</span></>}</td>
                      <td><code>{v.document}</code></td>
                      <td>{jour(v.edite_le)}</td>
                      <td>{v.signatures === 0
                        ? <span className="mention">aucune</span>
                        : `${v.signatures} valide(s)`}</td>
                      <td>
                        <span className={`etat ${
                          v.authentique ? 'vert' : v.alerte ? 'rouge' : v.courante ? 'bleu' : 'gris'}`}>
                          {v.etat}
                        </span>
                        {/* ⚠️ L'ALERTE LA PLUS COÛTEUSE DU §23 : une version signée
                            que la réédition a dépassée reste vérifiable par son QR.
                            Deux actes authentiques du même fonds circulent. */}
                        {v.alerte && <><br /><span className="erreur-inline">{v.alerte}</span></>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>}
    </section>
  );
}

/** Le motif d'un appel refusé, tel que le service l'a dit — jamais réécrit. */
function motifDe(e: unknown): string {
  return e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e);
}
